<template>
  <panel :title="'Quotation attachments' | translate" :buttons="[]" no-button panel-class="panel-services">
    <thux-table-component
      :instance="{}"
      instance-name="quotationattachment"
      :list="attachments"
      :fields="fields"
      :no-results-label="'There are no attachments to show'"
    >
      <template slot="is_external" slot-scope="row">
        <b-badge :variant="row.item.is_external ? 'info' : 'secondary'">
          {{ (row.item.is_external ? 'External attachment' : 'Internal document') | translate }}
        </b-badge>
      </template>
      <template slot="attachment" slot-scope="row">
        <b-btn v-if="row.item.attachment"
          variant="outline-primary"
          :title="'download' | translate"
          @click="downloadDocument(row.item)"
        >
          <i class="fas fa-download"></i>
        </b-btn>
      </template>
      <template slot="delete" slot-scope="row">
        <b-btn
          v-if="formEditableUpdate || attachmentEditEnabled"
          size="sm"
          variant="outline-danger"
          :title="'Delete' | translate"
          @click.prevent="confirmDelete(row.item)"
        >
          <i class="fas fa-trash"></i>
        </b-btn>
      </template>
    </thux-table-component>

    <div v-if="formEditableUpdate || attachmentEditEnabled" class="mt-3">
      <b-btn
        variant="primary"
        :disabled="!quotationId"
        @click.prevent="openModal()"
      >
        <i class="fas fa-plus mr-1"></i>{{ 'Add attachment' | translate }}
      </b-btn>
    </div>

    <!-- ADD MODAL -->
    <b-modal
      v-if="showModal"
      id="modalQuotationAttachment"
      ref="modalQuotationAttachment"
      :no-enforce-focus="true"
      :no-close-on-backdrop="true"
      :scrollable="true"
      size="lg"
      visible
      @hidden="closeModal"
    >
      <div slot="modal-title">
        {{ 'Add attachment' | translate }}
      </div>
      <b-form>
        <form-thux-horizontal-input
          :validator="$v.form.name"
          :label-form="'Name' | translate"
          class-form="col-lg-12 mb-2"
          label-cols-lg="3"
          label-cols-sm="4"
          :value="form.name"
          @change="$set(form, 'name', $event)"
        />
        <form-thux-horizontal-input
          :validator="$v.form.description"
          :label-form="'Description' | translate"
          class-form="col-lg-12 mb-2"
          label-cols-lg="3"
          label-cols-sm="4"
          :value="form.description"
          @change="$set(form, 'description', $event)"
        />
        <form-thux-horizontal-file
          :validator="$v.form.attachment"
          :label-form="'Attachment' | translate"
          class-form="col-lg-12 mb-2"
          label-cols-lg="3"
          label-cols-sm="4"
          :value="form.attachment"
          :placeholder="$t('Choose a file or drop it here...')"
          :drop-placeholder="$t('Drop file here...')"
          @input="$set(form, 'attachment', $event)"
        />
        <form-thux-horizontal-checkbox
          :validator="$v.form.is_external"
          :label-form="'Include in email (external attachment)' | translate"
          class-form="col-lg-12 my-2"
          label-cols-lg="10"
          label-cols-sm="8"
          :value="form.is_external"
          @change="$set(form, 'is_external', $event)"
        />
      </b-form>
      <div slot="modal-footer">
        <b-btn class="mr-2" variant="default" @click="closeModal">
          {{ 'Cancel' | translate }}
        </b-btn>
        <b-btn variant="primary" :disabled="$v.form.$invalid" @click="onSubmit">
          {{ 'Save' | translate }}
        </b-btn>
      </div>
    </b-modal>

    <confirm-dialog
      v-if="showDeleteDialog"
      :title="'Are you sure to remove this attachment?' | translate"
      @confirm-dialog-close="showDeleteDialog = false"
      @confirm-dialog-select="doDelete"
    />
  </panel>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import { required, maxLength } from 'vuelidate/lib/validators'
import { TYPES as QUOTATION_ATTACHMENT_TYPES } from '../../../quotation-attachment/store'
import ThuxTableComponent from '../../../../../components/thux-table/ThuxTableComponent'
import FormThuxHorizontalInput from '../../../../../components/form-thux-horizontal-components/FormThuxHorizontalInput'
import FormThuxHorizontalFileThux from '../../../../../components/form-thux-horizontal-components/FormThuxHorizontalFileThux'
import FormThuxHorizontalCheckbox from '../../../../../components/form-thux-horizontal-components/FormThuxHorizontalCheckbox'
import ConfirmDialog from '../../../../../components/ConfirmDialog'

export default {
  name: 'QuotationAttachmentsManager',
  components: {
    'thux-table-component': ThuxTableComponent,
    'form-thux-horizontal-input': FormThuxHorizontalInput,
    'form-thux-horizontal-file': FormThuxHorizontalFileThux,
    'form-thux-horizontal-checkbox': FormThuxHorizontalCheckbox,
    'confirm-dialog': ConfirmDialog
  },
  props: {
    quotationId: { default: null },
    attachmentEditEnabled: { type: Boolean, default: false },
    formEditableUpdate: { default: false }
  },
  data () {
    return {
      showModal: false,
      showDeleteDialog: false,
      attachmentToDelete: null,
      form: {}
    }
  },
  computed: {
    ...mapGetters({
      list: QUOTATION_ATTACHMENT_TYPES.GENERIC.quotation['quotation-attachment'].LIST.GETTERS.list
    }),
    attachments () {
      if (!this.list) return []
      return Array.isArray(this.list) ? this.list : (this.list.results || [])
    },
    fields () {
      const fields = [
        { key: 'name', label: this.$t('Name'), value: 'name' },
        { key: 'description', label: this.$t('Description'), value: 'description' },
        { key: 'is_external', label: this.$t('Type'), custom: true },
        { key: 'attachment', label: this.$t('Attachment'), custom: true }
      ]
      // The delete column only makes sense while editing: read-only mode allows download only.
      if (this.formEditableUpdate || this.attachmentEditEnabled) {
        fields.push({ key: 'delete', label: '', custom: true, thClass: 'text-right', tdClass: 'text-right' })
      }
      return fields
    }
  },
  watch: {
    quotationId: {
      immediate: true,
      handler (value) {
        if (value) this.loadList()
      }
    }
  },
  methods: {
    ...mapActions({
      ...QUOTATION_ATTACHMENT_TYPES.GENERIC.quotation['quotation-attachment'].LIST.ACTIONS,
      setFilters: QUOTATION_ATTACHMENT_TYPES.GENERIC.quotation['quotation-attachment'].LIST.ACTIONS.setFilters,
      createAttachment: QUOTATION_ATTACHMENT_TYPES.GENERIC.quotation['quotation-attachment'].DETAIL.ACTIONS.create,
      deleteAttachment: QUOTATION_ATTACHMENT_TYPES.GENERIC.quotation['quotation-attachment'].DETAIL.ACTIONS.delete
    }),
    loadList () {
      return this.setFilters({ filter__quotation: this.quotationId, no_page: 'no_page' })
    },
    fileName (path) {
      if (!path) return ''
      return decodeURIComponent(path.split('?')[0].split('/').pop())
    },
    openModal () {
      this.$set(this, 'form', { name: '', description: '', attachment: null, is_external: false })
      this.$set(this, 'showModal', true)
    },
    closeModal () {
      this.$set(this, 'showModal', false)
      this.$v.$reset()
    },
    onSubmit () {
      const data = {
        quotation: this.quotationId,
        name: this.form.name,
        description: this.form.description || '',
        is_external: !!this.form.is_external,
        attachment: this.form.attachment
      }
      this.createAttachment(data).then(() => {
        this.closeModal()
        this.loadList()
      })
    },
    confirmDelete (item) {
      this.$set(this, 'attachmentToDelete', item)
      this.$set(this, 'showDeleteDialog', true)
    },
    doDelete () {
      this.deleteAttachment({ id: this.attachmentToDelete.id }).then(() => {
        this.$set(this, 'showDeleteDialog', false)
        this.loadList()
      })
    },
    downloadDocument (item) {
      const fileName = `${item.name}.${item.simple_file_name.split('.').pop()}`
      this.download({ id: item.id, action: 'download-attachment', simpleFileName: fileName })
    }
  },
  validations: {
    form: {
      name: { required, maxLength: maxLength(255) },
      description: { maxLength: maxLength(255) },
      attachment: { required },
      is_external: {}
    }
  }
}
</script>
